import { type FormEvent, type ReactElement, useId, useState } from 'react';
import { type ApiError } from '@vello/api-client';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { AuthLayout } from '../components/layout';
import { Button, MfaDialog, AuthCard, Cluster, Stack, FormStack } from '@vello/web-theme';
import { AuthHeading, AuthPasswordField, AuthTextField, UiIcon } from '@vello/web-theme';
import { deviceFingerprint } from '../device';

/** Map an API error to a message id; the caller resolves it through the active locale. */
function errorMessageId(error: ApiError): string {
  switch (error.code) {
    case 'UNAUTHORIZED':
      return 'login.error.unauthorized';
    case 'FORBIDDEN':
      return 'login.error.forbidden';
    case 'RATE_LIMITED':
      return 'login.error.rateLimited';
    case 'NETWORK':
    case 'TIMEOUT':
      return 'login.error.network';
    default:
      return 'login.error.default';
  }
}

/** A pragmatic email-shape check to gate the sign-in button; the server remains authoritative. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Operator sign-in (S111), faithful to the Vello Claude Design A01/A01b: a light card for the credentials
 * and a **separate** MFA step (A01b) shown as a modal over the blurred card. The backend
 * (`establishOperatorSession`) verifies credential + MFA in one fail-closed call, so this two-step UI
 * collects the email/password first, then the MFA code, and submits them together — never a consumer OTP.
 */
export function LoginScreen(): ReactElement {
  const { login } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const emailId = useId();
  const passwordId = useId();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'credentials' | 'mfa'>('credentials');
  const [errorId, setErrorId] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const credentialsValid = EMAIL_RE.test(email.trim()) && password.length > 0;

  const goToMfa = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setErrorId(undefined);
    setStep('mfa');
  };

  const submitMfa = async (mfaCode: string): Promise<void> => {
    setErrorId(undefined);
    setBusy(true);
    const result = await login({ identifier: email, password, mfaCode, deviceFingerprint: await deviceFingerprint(), platform: 'web' });
    setBusy(false);
    if (!result.ok) setErrorId(errorMessageId(result.error));
    // On success the provider navigates to the dashboard.
  };

  const closeMfa = (): void => {
    setStep('credentials');
    setErrorId(undefined);
  };

  return (
    <AuthLayout>
      <AuthCard>
        <AuthHeading title={t('login.heading')} subtitle={t('login.subtitle')} />
        <FormStack gap={5} onSubmit={goToMfa} noValidate>
          <Stack gap={4}>
            <AuthTextField
              id={emailId}
              label={t('login.field.email')}
              icon="mail"
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              placeholder={t('login.field.email.placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <AuthPasswordField id={passwordId} label={t('login.field.password')} value={password} onChange={setPassword} showLabel={t('login.password.show')} hideLabel={t('login.password.hide')} />
          </Stack>
          <Cluster justify="end">
            <a
              href="/recovery"
              className="vello-link vello-ab-linkbtn"
              onClick={(e) => {
                e.preventDefault();
                navigate('recovery');
              }}
            >
              {t('login.forgot')}
            </a>
          </Cluster>
          <Button type="submit" variant="primary" block disabled={!credentialsValid}>
            {t('login.submit')}
            <UiIcon name="arrowRight" size={18} />
          </Button>
        </FormStack>
      </AuthCard>
      {/* The MFA step keeps the light A01b modal: the shared MfaDialog is portalled out of the navy panel (whose roles are re-pointed). */}
      <MfaDialog
        open={step === 'mfa'}
        title={t('mfa.title')}
        description={t('mfa.subtitle')}
        codeLabel={t('mfa.codeLabel')}
        submitLabel={t('mfa.submit')}
        busyLabel={t('mfa.submit.busy')}
        cancelLabel={t('mfa.back')}
        onSubmit={(code) => void submitMfa(code)}
        onCancel={closeMfa}
        busy={busy}
        error={errorId !== undefined ? t(errorId) : undefined}
      />
    </AuthLayout>
  );
}
